// Utilities to apply and persist theme using CSS variables
import api from '../services/api';

export function applyTheme(theme, palette) {
  const root = document.documentElement;
  if (theme) root.setAttribute('data-theme', theme);

  // Clear any inline vars first
  const tones = ['50','100','200','300','400','500','600','700','800','900'];
  tones.forEach(k => root.style.removeProperty(`--primary-${k}`));

  if (palette && typeof palette === 'object') {
    tones.forEach(k => {
      if (palette[k]) root.style.setProperty(`--primary-${k}`, palette[k]);
    });
  }
}

export function saveThemeLocal(theme, palette) {
  try {
    localStorage.setItem('site_theme', theme);
    if (palette) localStorage.setItem('site_palette', JSON.stringify(palette));
    else localStorage.removeItem('site_palette');
  } catch {}
}

export function loadThemeLocal() {
  try {
    const theme = localStorage.getItem('site_theme') || 'blue';
    const paletteRaw = localStorage.getItem('site_palette');
    const palette = paletteRaw ? JSON.parse(paletteRaw) : null;
    return { theme, palette };
  } catch {
    return { theme: 'blue', palette: null };
  }
}

export function hexToHsl(hex) {
  let r = 0, g = 0, b = 0;
  const m = hex.replace('#','');
  if (m.length === 3) {
    r = parseInt(m[0]+m[0],16); g = parseInt(m[1]+m[1],16); b = parseInt(m[2]+m[2],16);
  } else if (m.length === 6) {
    r = parseInt(m.substring(0,2),16);
    g = parseInt(m.substring(2,4),16);
    b = parseInt(m.substring(4,6),16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r,g,b), min = Math.min(r,g,b);
  let h=0, s=0, l=(max+min)/2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch(max){
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: Math.round(h*360), s: Math.round(s*100), l: Math.round(l*100) };
}

export function hslToHex(h, s, l){
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2*l - 1)) * s;
  const x = c * (1 - Math.abs((h/60) % 2 - 1));
  const m = l - c/2;
  let r=0,g=0,b=0;
  if (0<=h && h<60){ r=c; g=x; b=0; }
  else if (60<=h && h<120){ r=x; g=c; b=0; }
  else if (120<=h && h<180){ r=0; g=c; b=x; }
  else if (180<=h && h<240){ r=0; g=x; b=c; }
  else if (240<=h && h<300){ r=x; g=0; b=c; }
  else { r=c; g=0; b=x; }
  r = Math.round((r+m)*255).toString(16).padStart(2,'0');
  g = Math.round((g+m)*255).toString(16).padStart(2,'0');
  b = Math.round((b+m)*255).toString(16).padStart(2,'0');
  return `#${r}${g}${b}`;
}

export function generatePaletteFromBase(hex){
  const { h, s, l } = hexToHsl(hex);
  // Build tones by varying lightness and a touch of saturation.
  // Lightness chosen to guarantee WCAG AA contrast (4.5:1 on white) at -600
  // regardless of hue. Lightness 32 with most hues gives 5.5:1 - 9:1.
  const tones = {
    50:  97,
    100: 93,
    200: 86,
    300: 74,
    400: 60,
    500: 46,
    600: 32,
    700: 26,
    800: 20,
    900: 15,
  };
  const satAdjust = {
    50: -10, 100: -8, 200: -5, 300: -2, 400: 0, 500: 0,
    600: 2, 700: 3, 800: 4, 900: 6
  };
  const out = {};
  Object.keys(tones).forEach(k => {
    const nl = Math.max(5, Math.min(95, tones[k]));
    const ns = Math.max(20, Math.min(100, s + (satAdjust[k]||0)));
    out[k] = hslToHex(h, ns, nl);
  });
  return out;
}

export async function fetchAndApplyTheme(){
  try {
    const { data } = await api.get('/site-settings/theme');
    const theme = data.theme || 'blue';
    const palette = data.palette || null;
    applyTheme(theme, palette);
    saveThemeLocal(theme, palette);
  } catch (e) {
    const { theme, palette } = loadThemeLocal();
    applyTheme(theme, palette);
  }
}

export async function updateTheme(theme, palette){
  const res = await api.put('/site-settings/theme', { theme, palette });
  applyTheme(res.data.theme, res.data.palette || null);
  saveThemeLocal(res.data.theme, res.data.palette || null);
  return res.data;
}
